import type { PackFileSummary } from "@/api/packFiles";
import { cn } from "@/lib/cn";
import type { FileScope } from "@/lib/packFiles";
import { Input } from "@/ui/Input";
import { FileTree } from "@/ui/files/FileTree";

/** Search box, the scope filter row and the file tree. */
export function FileList({
  files,
  query,
  onQuery,
  selected,
  onSelect,
  tenantName,
  scope,
  onScope,
  scopeCounts,
  noteOf,
}: {
  files: readonly PackFileSummary[];
  query: string;
  onQuery: (query: string) => void;
  selected: string | null;
  onSelect: (path: string) => void;
  /** The project's tenant pack; null when it has none (the tenant filter is then hidden). */
  tenantName: string | null;
  scope: FileScope;
  onScope: (scope: FileScope) => void;
  scopeCounts: { all: number; tenant: number; mine: number };
  noteOf?: (file: PackFileSummary) => string | null;
}) {
  const options: { id: FileScope; label: string; count: number }[] = [
    { id: "all" as const, label: "All files", count: scopeCounts.all },
    ...(tenantName
      ? [
          {
            id: "tenant" as const,
            label: `${tenantName} changes`,
            count: scopeCounts.tenant,
          },
        ]
      : []),
    { id: "mine" as const, label: "My edits", count: scopeCounts.mine },
  ];
  return (
    <div className="space-y-2">
      <div
        role="group"
        aria-label="Show files"
        className="flex flex-wrap gap-1 text-xs"
      >
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={scope === option.id}
            onClick={() => onScope(option.id)}
            className={cn(
              "rounded-md border border-border px-2 py-1 font-medium text-muted-foreground hover:text-foreground",
              scope === option.id &&
                "bg-primary text-primary-foreground hover:text-primary-foreground",
            )}
          >
            {option.label} ({option.count})
          </button>
        ))}
      </div>
      <Input
        type="search"
        aria-label="Search files"
        placeholder="Search files"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
      />
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground">No files match.</p>
      ) : (
        <FileTree
          files={files}
          query={query}
          selected={selected}
          onSelect={onSelect}
          tenantName={tenantName ?? undefined}
          noteOf={noteOf}
        />
      )}
    </div>
  );
}
